/**
 * =============================================================================
 * SFS – Globale Module (window.sfs)
 * =============================================================================
 *
 * Module werden über das Attribut data-sfs-module am Container aktiviert und
 * beim Laden automatisch initialisiert (auch bei spätem Einbinden des Skripts).
 * Die Datei ist in eine IIFE gekapselt, global sichtbar ist nur window.sfs.
 *
 * Abhängigkeit (extern): decodeBase64Utf8(str) → für parseBase64Json (Wetter)
 *
 * Data-Attribute am Container:
 *   data-sfs-module      Modulname (window.sfs.<name>)
 *   data-interval-ms     Polling-Intervall in ms (Standard: Modul bzw. 60000)
 *   data-kat, data-filter  Query-Parameter für Bubble-Module (siehe BUBBLES)
 *
 * -----------------------------------------------------------------------------
 * FUNKTIONSÜBERSICHT
 * -----------------------------------------------------------------------------
 *
 * 1. Konfiguration
 *    - BASE_URL, ZURICH_TZ               API-Basis-URL (auch window.sfs.config.baseUrl), Zeitzone
 *    - WEATHER_URL                       Open-Meteo-Endpunkt (auch window.sfs.config.weatherUrl)
 *    - onReady                           Funktion ausführen, sobald das DOM bereit ist
 *
 * 2. Datums- und Zeit-Helfer (Europe/Zurich)
 *    - zurichClock                       Formatter-Factory für Zeitzonen-Teile
 *    - utils.parseIsoDate                ISO-Datum (YYYY-MM-DD) parsen
 *    - utils.getZurichToday              Heutiges Datum in Europe/Zurich
 *    - utils.getZurichSeconds            Sekunden seit Mitternacht in Europe/Zurich
 *    - utils.diffDays                    Kalendertage-Differenz (DST-sicher)
 *    - utils.loadCountdown               Countdown-Wert in Container schreiben
 *    - utils.startCountdownInterval      Aktualisierung täglich um Mitternacht (Zürich)
 *
 * 3. Generische Helfer
 *    - requestJson                       JSON laden (wirft bei Fehler)
 *    - loadJson                          JSON laden (null bei Fehler, 0 bei leer)
 *    - utils.fetchJson                   JSON laden (0 bei Fehler oder leer)
 *    - utils.startDataInterval           Sofortiger Aufruf + zyklisches Polling
 *                                        (pausiert in versteckten Tabs)
 *    - parseBase64Json                   Base64-kodiertes JSON parsen
 *    - getCell, setText                  Zelle lesen / Text in Kindelement setzen
 *      (auch als utils.getCell / utils.setText / utils.parseBase64Json erreichbar)
 *
 * 4. Modul-Factories
 *    - createModule                      Basis-Modul (Init + Polling, optional poll: false)
 *    - createBubbleModule                Modul für *_bubble_value-Endpunkte
 *
 * 5. Module (window.sfs.<name>)
 *    - weather                           Wetter (MeteoSwiss via Open-Meteo)
 *    - BUBBLES                           Tabelle aller Bubble-Module
 *    - countdown                         Tages-Countdown
 *    - timecountdown                     Zeit-Countdown (hh:mm:ss)
 *
 * 6. Initialisierung
 *    - initModules                       Alle [data-sfs-module] starten
 *
 * 7. Carsharing-Counter
 *    - initCarsharing                    Counter und Modal initialisieren
 * =============================================================================
 */

(function () {
  "use strict";

  // ===========================================================================
  // 1. Konfiguration
  // ===========================================================================

  const BASE_URL = "https://newsletter.sfs.biz/p/de.pinuts.sfs.api";
  const WEATHER_URL = "https://api.open-meteo.com/v1/forecast";
  const ZURICH_TZ = "Europe/Zurich";

  // Lokale Referenz: Setzt ein anderes Skript später window.sfs neu (z. B. "var sfs = {…}"),
  // bleiben die Module hier erhalten und werden in initModules wieder angehängt.
  const sfs = (window.sfs = window.sfs || {});
  const utils = (sfs.utils = sfs.utils || {});
  sfs.config = sfs.config || {};
  sfs.config.baseUrl = BASE_URL;
  sfs.config.weatherUrl = WEATHER_URL;

  const onReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  };

  // ===========================================================================
  // 2. Datums- und Zeit-Helfer (Europe/Zurich)
  // ===========================================================================

  // Liefert eine Funktion, die die aktuellen Zeitzonen-Teile als Zahlen zurückgibt
  const zurichClock = (options) => {
    const format = new Intl.DateTimeFormat("en-GB", Object.assign({ timeZone: ZURICH_TZ }, options));

    return () => {
      const parts = {};
      format.formatToParts(new Date()).forEach((p) => {
        parts[p.type] = Number(p.value);
      });
      return parts;
    };
  };

  const zurichDate = zurichClock({ year: "numeric", month: "2-digit", day: "2-digit" });
  const zurichTime = zurichClock({ hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });

  utils.parseIsoDate = (isoDate) => {
    const match = typeof isoDate === "string" && isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) } : null;
  };

  utils.getZurichToday = () => {
    const p = zurichDate();
    return { y: p.year, m: p.month, d: p.day };
  };

  utils.getZurichSeconds = () => {
    const p = zurichTime();
    return p.hour * 3600 + p.minute * 60 + p.second;
  };

  utils.diffDays = (a, b) => Math.floor((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000);

  // Schreibt die verbleibenden Tage in den Container (einmaliger Aufruf)
  utils.loadCountdown = (date, container) => {
    const target = utils.parseIsoDate(date);
    if (!target) return;

    const days = Math.max(0, utils.diffDays(utils.getZurichToday(), target));

    container.querySelectorAll(".keyvalue-1, .keyvalue-3").forEach((el) => {
      el.textContent = days;
    });
  };

  // Aktualisiert sofort und danach jeweils kurz nach Mitternacht (Zürich, DST-sicher).
  // Eigener Timer → das Modul braucht kein zusätzliches Polling.
  utils.startCountdownInterval = (date, container) => {
    if (!date) return;

    clearTimeout(container._ci);

    const update = () => {
      utils.loadCountdown(date, container);
      container._ci = setTimeout(update, (86400 - utils.getZurichSeconds()) * 1000 + 1000);
    };

    update();
  };

  // ===========================================================================
  // 3. Generische Helfer
  // ===========================================================================

  const requestJson = (url, cache = "no-cache") =>
    fetch(url, { cache }).then((res) => {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    });

  // Liefert: null bei Fehler, 0 bei leerer Antwort, sonst die Daten
  const loadJson = (url) =>
    requestJson(url)
      .then((data) =>
        data == null || (typeof data === "object" && Object.keys(data).length === 0) ? 0 : data
      )
      .catch((err) => {
        console.error("Fetch-Fehler:", err);
        return null;
      });

  // Öffentliche Variante (wie bisher): 0 bei Fehler oder leerer Antwort
  utils.fetchJson = (url) => loadJson(url).then((data) => (data === null ? 0 : data));

  // Wird ein Tab wieder sichtbar, werden überfällige Module sofort aktualisiert
  const visibilityRefreshers = new Set();

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) visibilityRefreshers.forEach((fn) => fn());
  });

  utils.startDataInterval = (opts) => {
    const container = opts.container;
    const key = "_i_" + container.dataset.sfsModule;

    clearInterval(container[key]);
    visibilityRefreshers.delete(container[key + "_v"]);

    const run = () => {
      container[key + "_t"] = Date.now();
      opts.loadDataFn();
    };

    // Im versteckten Tab wird nicht gepollt
    const tick = () => {
      if (!document.hidden) run();
    };

    // Nur nachladen, wenn das letzte Laden länger als ein Intervall her ist
    const onVisible = () => {
      if (Date.now() - (container[key + "_t"] || 0) >= opts.intervalMs) run();
    };

    run(); // Sofortiger erster Aufruf, danach zyklisch
    container[key] = setInterval(tick, opts.intervalMs);
    container[key + "_v"] = onVisible;
    visibilityRefreshers.add(onVisible);
  };

  let decodeWarned = false;

  const parseBase64Json = (str, fallback = {}) => {
    if (!str) return fallback;

    if (typeof decodeBase64Utf8 !== "function") {
      if (!decodeWarned) {
        console.error("[sfs] Externe Funktion decodeBase64Utf8() fehlt – Base64-Daten werden ignoriert.");
        decodeWarned = true;
      }
      return fallback;
    }

    try {
      return JSON.parse(decodeBase64Utf8(str)) || fallback;
    } catch (e) {
      console.warn("JSON parse failed:", e);
      return fallback;
    }
  };

  // Liest data[row][col]; liefert 0, wenn nicht vorhanden
  const getCell = (data, row = 0, col = 0) => {
    const line = data && data[row];
    return line && line[col] != null ? line[col] : 0;
  };

  const setText = (container, selector, value) => {
    const el = container.querySelector(selector);
    if (el) el.textContent = value;
  };

  Object.assign(utils, { getCell, setText, parseBase64Json });

  // ===========================================================================
  // 4. Modul-Factories
  // ===========================================================================

  // opts.url(cfg):      API-URL (ohne url wird processData ohne Abruf ausgeführt)
  // opts.processData:   (data, container, cfg) => void
  // opts.intervalMs:    Standard-Intervall (überschreibbar per data-interval-ms)
  // opts.poll:          false → processData nur einmal ausführen (Modul hat eigenen Timer)
  const createModule = (opts) => ({
    init(cfg) {
      const load = () => {
        if (!opts.url) {
          opts.processData(null, cfg.container, cfg);
          return;
        }

        loadJson(opts.url(cfg)).then((data) => opts.processData(data, cfg.container, cfg));
      };

      if (opts.poll === false) {
        load();
        return;
      }

      utils.startDataInterval({
        container: cfg.container,
        intervalMs: cfg.intervalMs || opts.intervalMs || 60000,
        loadDataFn: load,
      });
    },
  });

  // Schreibt data[0][0..valueCount-1] in .keyvalue-1 .. .keyvalue-N
  // params: Namen der Query-Parameter (aus cfg), z. B. ["kat", "filter"]
  // Bei einem Netzwerkfehler (data === null) bleibt der letzte Wert stehen.
  const createBubbleModule = (endpoint, valueCount = 1, params = []) =>
    createModule({
      url: (cfg) =>
        BASE_URL +
        "/" +
        endpoint +
        (params.length
          ? "?" + params.map((key) => key + "=" + encodeURIComponent(cfg[key] || "")).join("&")
          : ""),
      processData: (data, container) => {
        if (data === null) return;

        for (let i = 0; i < valueCount; i++) {
          setText(container, ".keyvalue-" + (i + 1), getCell(data, 0, i));
        }
      },
    });

  // ===========================================================================
  // 5. Module
  // ===========================================================================

  // -- Wetter -----------------------------------------------------------------
  sfs.weather = createModule({
    intervalMs: 1800000, // 30 Minuten
    processData: (_data, container) => {
      if (!container.querySelector(".keyvalue-1")) return;

      const ds = container.dataset;
      const parts = (ds.weatherDate || "").split("|");
      const date = parts[0] || null;
      const timeMatch = (parts[1] || "").match(/^(\d{1,2}):\d{2}/);
      // Open-Meteo liefert stündliche Werte → auf volle Stunde abrunden (14:30 → 14:00)
      const time = timeMatch ? timeMatch[1].padStart(2, "0") + ":00" : null;
      const wmo = parseBase64Json(ds.weatherWmo);
      const wmoIcon = parseBase64Json(ds.weatherWmoIcon);
      const showTemp = ds.weatherTemp !== "false";

      if (date) {
        const target = utils.parseIsoDate(date);

        if (!target || utils.diffDays(utils.getZurichToday(), target) > 5) {
          setText(container, ".keyvalue-1", target ? "Noch keine Vorhersage" : "–");
          return;
        }
      }

      const fields = (extra) => (showTemp ? "weather_code," + extra : "weather_code");
      const range = "&start_date=" + date + "&end_date=" + date;

      let query = "&current_weather=true";
      if (date && time) query = "&hourly=" + fields("temperature_2m") + range;
      else if (date) query = "&daily=" + fields("temperature_2m_max,temperature_2m_min") + range;

      const url =
        WEATHER_URL +
        "?latitude=" +
        encodeURIComponent(ds.lat || "47.41") +
        "&longitude=" +
        encodeURIComponent(ds.lon || "9.63") +
        "&timezone=Europe%2FZurich&models=meteoswiss_icon_ch2" +
        query;

      const pick = (arr, i) => (arr && i >= 0 && i < arr.length ? arr[i] : null);

      const extractData = (data) => {
        if (date && time) {
          const hourly = data.hourly || {};
          const idx = hourly.time ? hourly.time.indexOf(date + "T" + time) : -1;

          return {
            code: pick(hourly.weather_code || hourly.weathercode, idx),
            temp: pick(hourly.temperature_2m, idx),
          };
        }

        if (date) {
          const daily = data.daily || {};

          return {
            code: pick(daily.weather_code || daily.weathercode, 0),
            temp: pick(daily.temperature_2m_max, 0),
            tempMin: pick(daily.temperature_2m_min, 0),
          };
        }

        const current = data.current_weather;
        return { code: current ? current.weathercode : null, temp: current ? current.temperature : null };
      };

      const formatTemp = (temp, tempMin) => {
        if (temp == null) return "";
        return tempMin != null ? Math.round(tempMin) + "°–" + Math.round(temp) + "°" : Math.round(temp) + "°C";
      };

      const renderIcon = (el, src, label) => {
        el.textContent = "";
        if (!src) return;

        const img = document.createElement("img");
        img.src = src;
        img.alt = label;
        img.style.width = "var(--weather-icon-size, 64px)";
        img.style.height = "auto";
        el.appendChild(img);
      };

      loadJson(url)
        .then((data) => {
          // Fehler (null) oder leere Antwort (0)
          if (!data) {
            setText(container, ".keyvalue-1", "–");
            return;
          }

          const result = extractData(data);
          const key = result.code != null ? String(result.code) : null;
          const label = (key && wmo[key]) || "–";
          const icon = key ? wmoIcon[key] : null;
          const tempStr = showTemp ? formatTemp(result.temp, result.tempMin) : "";
          const iconEl = container.querySelector(".icon");

          if (iconEl) renderIcon(iconEl, icon, label);
          setText(container, ".keyvalue-1", icon ? tempStr : (label + " " + tempStr).trim());
        })
        .catch((err) => {
          console.warn("[weather] Darstellungsfehler:", err);
          setText(container, ".keyvalue-1", "–");
        });
    },
  });

  // -- Bubble-Module (API) ----------------------------------------------------
  // [Modulname, Endpunkt, Anzahl Werte, Query-Parameter]
  const BUBBLES = [
    ["bsfevening", "bsf_evening_bubble_value"],
    ["orderdmineralheilbadabos", "mineralheilbad_abos_bubble_value"],
    ["orderdfashion", "fashion_orders_bubble_value", 1, ["filter"]],
    ["pensionaerstreffen", "pensionaers_treffen_bubble_value"],
    ["skiday", "skiday_bubble_value", 1, ["kat"]],
    ["openair", "openair_bubble_value", 2],
    ["bicycleday", "bicycleday_bubble_value", 2],
    ["bowling", "bowling_bubble_value"],
    ["gsohllauf", "gsohllauf_bubble_value"],
    ["churfirsten", "churfirsten_bubble_value"],
    ["itdevices", "it_device_bubble_value", 1, ["kat", "filter"]],
    ["gv", "gv_bubble_value"],
    ["carsharing", "carsharing_bubble_value"],
    ["carsharingreport", "carsharing_report_bubble_value", 1, ["filter"]],
    ["wctdsurvey", "wctd_survey_bubble_value", 1, ["filter"]],
  ];

  BUBBLES.forEach((def) => {
    sfs[def[0]] = createBubbleModule(def[1], def[2], def[3]);
  });

  // -- Countdown (Tage) -------------------------------------------------------
  // Eigener Mitternachts-Timer → kein Polling nötig
  sfs.countdown = createModule({
    poll: false,
    processData: (_data, container) => utils.startCountdownInterval(container.dataset.countdownDate, container),
  });

  // -- Zeit-Countdown (hh:mm:ss, Zeitzone Europe/Zurich) ----------------------
  // Eigener Sekunden-Timer → kein Polling nötig. Stoppt bei 0:00:00.
  sfs.timecountdown = createModule({
    poll: false,
    processData: (_data, container) => {
      const [hourStr, minuteStr] = (container.dataset.targetTime || "").split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (isNaN(hour) || isNaN(minute)) return;

      const targetSeconds = hour * 3600 + minute * 60;
      const pad = (n) => String(n).padStart(2, "0");

      const update = () => {
        const diff = Math.max(0, targetSeconds - utils.getZurichSeconds());
        setText(container, ".keyvalue-1", Math.floor(diff / 3600) + ":" + pad(Math.floor((diff % 3600) / 60)) + ":" + pad(diff % 60));

        if (diff === 0) {
          clearInterval(container._tc);
          container._tc = null;
        }
      };

      clearInterval(container._tc);
      container._tc = setInterval(update, 1000);
      update();
    },
  });

  // ===========================================================================
  // 6. Initialisierung
  // ===========================================================================

  const initModules = () => {
    // Wurde window.sfs inzwischen ersetzt, fehlende Einträge wieder anhängen
    // (vorhandene Einträge des anderen Skripts bleiben unverändert)
    if (window.sfs !== sfs) {
      window.sfs = window.sfs || {};
      Object.keys(sfs).forEach((key) => {
        if (!(key in window.sfs)) window.sfs[key] = sfs[key];
      });
    }

    document.querySelectorAll("[data-sfs-module]").forEach((el) => {
      const module = sfs[el.dataset.sfsModule];

      if (module && module.init) {
        module.init({
          container: el,
          intervalMs: parseInt(el.dataset.intervalMs, 10) || 0, // 0 → Modul-Standard
          kat: el.dataset.kat || "",
          filter: el.dataset.filter || "",
        });
      }
    });
  };

  // ===========================================================================
  // 7. Carsharing-Counter
  // ===========================================================================

  const initCarsharing = () => {
    const INTERVAL = 10000;
    const BUTTON = ".carsharing_passenger_action";
    const INITS_KEY = "contact_inits";

    let modal = document.querySelector("[data-carsharing-modal]");
    const input = modal ? modal.querySelector("[data-modal-inits]") : null;
    const errorEl = modal ? modal.querySelector(".carsharing-modal-error") : null;

    // Unvollständiges Modal wie fehlendes Modal behandeln (verhindert Abstürze)
    if (modal && (!input || !errorEl)) {
      console.error("[carsharing] Modal unvollständig: [data-modal-inits] oder .carsharing-modal-error fehlt.");
      modal = null;
    }

    let timer = null;
    let polling = false;
    let activeButton = null;
    let returnFocusTo = null;

    // Zeitpunkt der letzten eigenen Aktion je Zähler (verhindert Überschreiben durch ältere Poll-Daten)
    const lastAction = new Map();
    const counterKey = (id, pos) => String(id) + "|" + String(pos || "");

    const isCarsharingActive = () => !!document.querySelector("[data-module-carsharing]");
    const isModalOpen = () => !!modal && modal.classList.contains("is-open");

    const readInits = () => {
      try {
        return (localStorage.getItem(INITS_KEY) || "").trim();
      } catch (e) {
        return "";
      }
    };

    const saveInits = (value) => {
      try {
        localStorage.setItem(INITS_KEY, value);
      } catch (e) {
        // Speicher nicht verfügbar (z. B. privater Modus) → ignorieren
      }
    };

    const updateCounter = (id, pos, value) => {
      if (id == null) return;

      const base = BUTTON + '[data-id="' + CSS.escape(String(id)) + '"]';
      const p = String(pos || "");

      // Leere Position: Buttons ohne data-pos UND mit leerem data-pos treffen
      const selector = p
        ? base + '[data-pos="' + CSS.escape(p) + '"]'
        : base + ":not([data-pos])," + base + '[data-pos=""]';

      document.querySelectorAll(selector).forEach((button) => {
        const element = button.parentElement && button.parentElement.querySelector(".carsharing_passenger_counter");
        if (element) element.textContent = value;
      });
    };

    const showError = (message) => {
      errorEl.textContent = message;
      errorEl.hidden = !message;
      input.classList.toggle("is-invalid", !!message);

      if (message) {
        input.setAttribute("aria-invalid", "true");
        input.focus();
      } else {
        input.removeAttribute("aria-invalid");
      }
    };

    const closeModal = () => {
      modal.classList.remove("is-open");
      if (activeButton) activeButton.dataset.busy = "";
      activeButton = null;
      showError("");

      // Fokus an den auslösenden Button zurückgeben
      if (returnFocusTo && document.contains(returnFocusTo)) returnFocusTo.focus();
      returnFocusTo = null;
    };

    const openModal = (button) => {
      activeButton = button;
      returnFocusTo = button;

      const prefix = button.dataset.action === "-" ? "remove" : "add";
      [
        ["Title", "[data-modal-title]"],
        ["Text", "[data-modal-text]"],
        ["Button", "[data-modal-confirm]"],
      ].forEach((entry) => {
        const el = modal.querySelector(entry[1]);
        if (el) el.textContent = modal.dataset[prefix + entry[0]] || "";
      });

      input.value = readInits();
      showError("");
      modal.classList.add("is-open");
      input.focus();
    };

    const executeAction = (button, inits) => {
      const id = button.dataset.id;
      const pos = button.dataset.pos || "";
      const action = button.dataset.action || "+";

      if (!id || !inits) {
        button.dataset.busy = "";
        return;
      }

      button.dataset.busy = "1";
      lastAction.set(counterKey(id, pos), Date.now());

      requestJson(
        BASE_URL +
          "/carsharing_passenger_counter?id=" + encodeURIComponent(id) +
          "&pos=" + encodeURIComponent(pos) +
          "&action=" + encodeURIComponent(action) +
          "&inits=" + encodeURIComponent(inits),
        "no-store"
      )
        .then((rows) => {
          if (rows && rows[0] && rows[0][0] != null) {
            updateCounter(id, pos, rows[0][0]);
            lastAction.set(counterKey(id, pos), Date.now());
          }
        })
        .catch((e) => console.error("[carsharing]", e))
        .finally(() => {
          button.dataset.busy = "";
        });
    };

    const submitModal = () => {
      // Getrimmten Wert zurückschreiben, damit checkValidity() nicht an Leerzeichen scheitert
      const value = input.value.trim();
      input.value = value;

      showError("");

      if (!value) {
        showError(modal.dataset.requiredError || "");
        return;
      }

      if (!input.checkValidity()) {
        showError(modal.dataset.invalidInitsError || "");
        return;
      }

      saveInits(value);

      // activeButton vor closeModal lösen, damit busy während des Requests gesetzt bleibt
      const button = activeButton;
      activeButton = null;
      closeModal();

      if (button) executeAction(button, value);
    };

    const handleClick = (event) => {
      if (!isCarsharingActive()) return;

      const button = event.target instanceof Element ? event.target.closest(BUTTON) : null;
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      if (button.dataset.busy === "1") return;

      const inits = readInits();

      // Ohne gespeicherte Inits wird das Modal benötigt
      if (!inits && !modal) {
        console.error("[carsharing] Modal [data-carsharing-modal] fehlt im DOM.");
        return;
      }

      button.dataset.busy = "1";

      if (inits) {
        executeAction(button, inits);
      } else {
        openModal(button);
      }
    };

    const pollStatus = () => {
      if (!isCarsharingActive() || document.hidden || polling) return;

      polling = true;

      requestJson(BASE_URL + "/carsharing_passenger_counter_status", "no-store")
        .then((rows) => {
          if (!Array.isArray(rows)) return;

          const now = Date.now();

          rows.forEach((row) => {
            if (!Array.isArray(row) || row.length < 3) return;

            // Kürzlich selbst geänderte Zähler nicht mit evtl. veralteten Poll-Daten überschreiben
            const t = lastAction.get(counterKey(row[0], row[1]));
            if (t && now - t < INTERVAL) return;

            updateCounter(row[0], row[1], row[2]);
          });
        })
        .catch((e) => console.error("[carsharing]", e))
        .finally(() => {
          polling = false;

          // Auch später eingefügte Zähler werden sichtbar geschaltet
          document.querySelectorAll(".carsharing_passenger_counter").forEach((el) => el.classList.add("is-ready"));
        });
    };

    const startPolling = () => {
      if (timer || !isCarsharingActive()) return;

      pollStatus();
      timer = setInterval(pollStatus, INTERVAL);
    };

    const stopPolling = () => {
      clearInterval(timer);
      timer = null;
    };

    if (modal) {
      if (!modal.hasAttribute("role")) modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");

      const closeBtn = modal.querySelector("[data-modal-close]");
      const confirmBtn = modal.querySelector("[data-modal-confirm]");

      if (closeBtn) closeBtn.addEventListener("click", closeModal);
      if (confirmBtn) confirmBtn.addEventListener("click", submitModal);

      input.addEventListener("input", () => showError(""));

      input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          submitModal();
        }
      });

      // Escape schliesst das Modal unabhängig davon, wo der Fokus liegt
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && isModalOpen()) {
          event.preventDefault();
          closeModal();
        }
      });

      modal.addEventListener("click", (event) => {
        if (event.target === modal) closeModal();
      });
    }

    document.addEventListener("click", handleClick, true);
    document.addEventListener("visibilitychange", () => (document.hidden ? stopPolling() : startPolling()));

    startPolling();
  };

  onReady(() => {
    initModules();
    initCarsharing();
  });
})();